<script lang="ts">
  import Dropdown from "$app/components/Dropdown.svelte"
  import { fly } from "svelte/transition"
  import { SyncLoader } from "svelte-loading-spinners"
  import { xhr, toast, getAvatar } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import Notification from "./Notification.svelte"
  import { Ellipsis } from "lucide-svelte"
  import { appStore } from "$app/store.svelte"
  import { onMount } from "svelte"

  interface Props {
    open?: boolean
    closeOnEsc?: boolean
    onclose?: () => void
  }

  let { open = $bindable(false), closeOnEsc = true, onclose }: Props = $props()

  let openDropdown = $state(false)
  let isLoading = $state(true)
  let background: HTMLElement = $state()
  let wrap: HTMLElement = $state()

  let filter = $state("all")

  let x = screen.width * 0.5

  let avatars: AvatarType[] = []

  async function loadNotifications(notType: string): Promise<void> {
    isLoading = true

    try {
      const resp = await xhr.get(`/api/v1/notifications.json?${notType}=1`)
      if (resp.data.notifications.length === 0) {
        isLoading = false
        return
      }

      const user_ids = Array.from(
        new Set(
          resp.data.notifications.map(
            (n) => n.params?.user?.id || n.params?.sender?.id
          )
        )
      )

      if (user_ids.length === 0) {
        isLoading = false
        return
      }

      const ids_query = user_ids.map((id) => `ids[]=${id}`).join("&")

      const avatars = await xhr.get(`/api/v1/users/avatars?${ids_query}`)
      appStore.notifications = resp.data.notifications.map((noti) => {
        const user_id = noti.params?.user?.id || noti.params?.sender?.id
        return {
          ...noti,
          user_avatar_url: getAvatar(user_id, avatars.data)
        }
      })
    } catch (e) {
      isLoading = false
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error loading your notifications", "error")
    } finally {
      isLoading = false
    }
  }

  function unreadNotifications() {
    return appStore.notifications.filter(
      (notification) => !notification.read_at
    )
  }

  async function markAllAsRead(): Promise<void> {
    openDropdown = false
    try {
      const resp = await xhr.patch(
        `/api/v1/notifications/mark_all_as_read.json`,
        {}
      )

      if (resp.data.success) {
        appStore.notifications = appStore.notifications.map((el) => {
          el.read_at = new Date()
          return el
        })
      }
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error updating your notifications", "error")
    }
  }

  function close() {
    open = false
    onclose()
  }

  const handleKeyup = ({ key }) => {
    if (closeOnEsc && key === "Escape") {
      close()
    }
  }

  let loaded = $state(false)
  $effect(() => {
    if (open && !loaded) {
      loadNotifications("all")
      loaded = true
    }
  })

  onMount(async () => {
    loadNotifications("unread")
  })
</script>

<svelte:window
  onkeyup={(e) => {
    e.preventDefault()
    handleKeyup(e)
  }}
/>

{#if open}
  <div
    class="relative z-30"
    bind:this={background}
    aria-labelledby="slide-over-title"
    role="dialog"
    aria-modal="true"
  >
    <!-- Background backdrop, show/hide based on slide-over state. -->
    <div class="fixed inset-0"></div>

    <div class="fixed inset-0 overflow-hidden">
      <div
        class="absolute inset-0 overflow-hidden"
        transition:fly={{ x: x, opacity: 1, duration: 1000 }}
        bind:this={wrap}
      >
        <div
          class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16"
        >
          <!--
          Slide-over panel, show/hide based on slide-over state.

          Entering: "transform transition ease-in-out duration-500 sm:duration-700"
            From: "translate-x-full"
            To: "translate-x-0"
          Leaving: "transform transition ease-in-out duration-500 sm:duration-700"
            From: "translate-x-0"
            To: "translate-x-full"
        -->
          <div class="pointer-events-auto w-screen max-w-md">
            <div
              class="flex h-full flex-col overflow-y-auto bg-white shadow-xl"
            >
              <div class="p-6">
                <div class="flex items-start justify-between">
                  <h2
                    class="text-2xl font-semibold leading-6 text-gray-900 flex items-center"
                    id="slide-over-title"
                  >
                    Notifications
                    <div class="relative inline-block text-left ml-4">
                      <Dropdown bind:open={openDropdown}>
                        {#snippet button()}
                          <span data-name="task-context-menu">
                            <Ellipsis
                              class="text-gray-800 fill-current w-8 h-8"
                            />
                          </span>
                        {/snippet}

                        <div>
                          <div
                            class="origin-top-right absolute -right-28 mt-0 w-40 rounded-md shadow-lg bg-white ring-1 ring-gray-300 ring-opacity-5 focus:outline-none z-10"
                            role="menu"
                          >
                            <div class="py-1" role="none">
                              <button
                                onclick={() => {
                                  markAllAsRead()
                                }}
                                class="btn-dropdown"
                                role="menuitem"
                              >
                                Mark all as read</button
                              >
                              <!--
                              <button
                                class="btn-dropdown"
                                role="menuitem"
                                on:click={() => {
                                  //openTaskDropdown = false
                                  //duplicateTask()
                                }}
                              >
                                Notification Settings
                              </button>
                              -->
                            </div>
                          </div>
                        </div>
                      </Dropdown>
                    </div>
                  </h2>

                  <div class="ml-3 flex h-7 items-center">
                    <button
                      type="button"
                      onclick={close}
                      class="relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:ring-2 focus:ring-indigo-500"
                    >
                      <span class="absolute -inset-2.5"></span>
                      <span class="sr-only">Close panel</span>
                      <svg
                        class="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              {#if isLoading}
                <div class="py-12 flex justify-center w-full">
                  <SyncLoader
                    size="240"
                    color="#7572d2"
                    unit="px"
                    duration="1s"
                  />
                </div>
              {:else}
                <div class="border-b border-gray-200">
                  <div class="px-6">
                    <!-- Tab component -->
                    <nav class="-mb-px flex space-x-6">
                      <!-- Current: "border-indigo-500 text-indigo-600", Default: "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700" -->
                      <button
                        onclick={() => (filter = "all")}
                        class="{filter === 'all'
                          ? 'border-indigo-500 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'} whitespace-nowrap border-b-2 px-1 pb-4 text-sm font-medium"
                        >All</button
                      >
                      <button
                        onclick={() => (filter = "unread")}
                        class="{filter === 'unread'
                          ? 'border-indigo-500 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'} whitespace-nowrap border-b-2 px-1 pb-4 text-sm font-medium"
                        >Unread</button
                      >
                    </nav>
                  </div>
                </div>
                <ul
                  role="list"
                  class="flex-1 divide-y divide-gray-200 overflow-y-auto"
                >
                  {#each filter === "all" ? appStore.notifications : unreadNotifications() as notification}
                    <Notification {notification} />
                  {:else}
                    <div class="text-lg py-8 px-6 text-gray-600">
                      {#if filter === "all"}
                        You have no notifications at this time
                      {:else}
                        You have no unread notifications at this time
                      {/if}
                    </div>
                  {/each}
                </ul>
              {/if}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
{/if}
